import React from 'react';

import { formatLinkBlockToUrl } from '$utilities/format.js';
import { Gear } from '$partials/ui/index.js';
import { IconArrowRight } from '$partials/icon/index.js';
import { isLocalURL } from '$utilities/browser';
import { PageShell } from '$partials/global/index.js';

/* ========================================================================= */
/* Utilities */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').GearCategoryAnchors}
 */
const anchorCategories = (content) => {
    const taken = new Set();

    return content.map((category, position) => {
        const slug = String(category?.heading || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        const base = slug ? `gear-${slug}` : `gear-${position + 1}`;

        let anchorId = base;
        let suffix = 2;

        /* Two categories named the same would otherwise send both sidebar
           entries to the first of them. */
        while (taken.has(anchorId)) {
            anchorId = `${base}-${suffix}`;
            suffix += 1;
        }

        taken.add(anchorId);

        return { ...category, anchorId };
    });
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').GearListingPage}
 */
const GearListingPage = (props) => {
    const page = props.data?.page;

    const categories = anchorCategories(page?.content || []);

    const ctaHref = formatLinkBlockToUrl(page?.ctaLink);
    const isCtaExternal = ctaHref && !isLocalURL(ctaHref);

    return (
        <PageShell
            banner
            footer={props.data?.footer}
            header={props.data?.header}>
            <div className='layout-container bg-neutral-00'>
                <div className='py-16 sm:py-40'>
                    <div className='layout-grid flex-col sm:flex-row'>
                        {/* Page header and category navigation */}
                        <div className='w-full sm:w-4/12 lg:w-3/12'>
                            <div className='sm:sticky sm:top-110'>
                                <div className='layout-grid-col'>
                                    <h1 className='type-style-heading'>
                                        {page?.title}
                                    </h1>

                                    {page?.introduction && (
                                        <div
                                            className='rich-text type-style-heading'
                                            dangerouslySetInnerHTML={{ __html: page.introduction }} />
                                    )}
                                </div>

                                <div className='layout-grid-col sm:w-3/4 lg:w-2/3'>
                                    <div>
                                        {categories.length > 0 && (
                                            <nav
                                                aria-label='Categories'
                                                className='hidden sm:block sm:mt-32'>
                                                <ul>
                                                    {categories.map((category) => (
                                                        <li
                                                            className='border-t border-neutral-400'
                                                            key={category.id}>
                                                            <a
                                                                className='block py-12 type-style-body'
                                                                href={`#${category.anchorId}`}>
                                                                {category.heading}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </nav>
                                        )}

                                        {(page?.ctaLabel && ctaHref) && (
                                            <p className='mb-48 sm:mb-0 mt-24 sm:mt-32'>
                                                <a
                                                    className='button type-style-body cursor-pointer text-nowrap'
                                                    href={ctaHref}
                                                    rel={isCtaExternal ? 'noopener noreferrer' : undefined}
                                                    target={isCtaExternal ? '_blank' : undefined}>
                                                    {page.ctaLabel}

                                                    <span
                                                        aria-hidden={true}
                                                        className='ml-5'>
                                                        <IconArrowRight
                                                            height={20}
                                                            width={20} />
                                                    </span>
                                                </a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* The catalogue, a category per Gear module */}
                        {categories.length > 0 && (
                            <div className='w-full sm:w-8/12'>
                                {/* Push heading down */}
                                <div
                                    aria-hidden={true}
                                    className='hidden sm:block sm:w-4/8 lg:w-3/8 text-transparent type-style-heading select-none pointer-events-none'>
                                    {page?.title}
                                </div>

                                <div className='flex flex-col gap-y-28 md:gap-y-48'>
                                    {categories.map((category) => (
                                        <Gear
                                            anchorId={category.anchorId}
                                            heading={category.heading}
                                            key={category.id}
                                            rows={category.rows} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageShell>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default GearListingPage;
